<template>
  <component
    :is="teaser.url ? 'a' : 'div'"
    :href="teaser.url"
    class="menu-teaser menu-teaser__image"
    :class="{
      [$class('hidden')]: !result && !loading,
      [$class('loading')]: loading,
    }"
    :style="{
      '--color-background': `${teaser.colorBackground}`,
      '--color-text': `${teaser.colorText}`,
      '--color-text-cta': `${teaser.colorTextCta}`,
      '--color-background-cta': `${teaser.colorBackgroundCta}`,
    }"
    @click="handleTrackingTeaser(teaser, 'select_promotion')"
  >
    <div class="menu-teaser__image-container">
      <p class="visually-hidden" v-text="teaser.image.alt" />

      <responsive-image
        :image="teaser.image"
        :ratio="desktop ? '5:6' : '12:5'"
        sizes="(min-width: 1024px) 305px, 300px"
        widths="305, 610, 915"
      />

      <div class="menu-teaser__content">
        <p
          class="menu-teaser__title text-desktop-display-h3"
          :class="teaser?.fontSizeHeadline"
          v-text="teaser.title"
        />

        <p
          v-if="desktop"
          class="menu-teaser__subtitle text-desktop-paragraph-xs-regular"
          v-text="teaser.subtitle"
        />

        <div v-if="teaser.code" class="menu-teaser__promo">
          <div class="menu-teaser__promo-code">
            <div v-text="teaser.promoText" />

            <button
              ref="copyButton"
              class="menu-teaser__code"
              type="button"
              @click.prevent="handleCopy"
            >
              <span v-text="teaser.code" />

              <span
                ref="tooltip"
                class="tooltip text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular"
                :class="{ [$class('active')]: showTooltip }"
                v-text="$string('general.hero_teaser.tooltip_text')"
              />
            </button>
          </div>
        </div>

        <btn
          v-else-if="teaser.ctaText && teaser.url"
          class="menu-teaser__cta"
          custom-element="p"
          size="small"
          modifiers="secondary center"
          :class-label="teaser?.fontSizeButton"
          :label="teaser.ctaText"
        />
      </div>
    </div>
  </component>
</template>

<script>
/**
 * Vue: Menu teaser (menu-teaser)
 * -----------------------------------------------------------------------------
 * Mobile Menu teaser.
 *
 * @param {Boolean} desktop - Show at desktop sizes.
 * @param {String} position - Menu teaser position.
 * @param {Boolean} isViewed - Menu teaser has been viewed.
 * @param {Object} teaser - Teaser object.
 *
 */
import Btn from '~global/btn/btn.vue';
import ResponsiveImage from '~global/images/responsive-image';

import { dateTime } from '~/helpers/general';

export default {
  name: 'MenuTeaser',

  components: {
    ResponsiveImage,
    Btn,
  },

  props: {
    desktop: {
      type: Boolean,
      default: false,
    },
    position: {
      type: String,
      default: 'header-navigation',
    },
    isViewed: {
      type: Boolean,
      default: false,
    },
    teaser: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: {
    'update-menu-drawer-item-refs': () => true,
  },

  data() {
    return {
      showTooltip: false,
      loading: true,
      result: false,
    };
  },

  watch: {
    result(newValue, oldValue) {
      if (newValue === oldValue) {
        return;
      }

      this.$emit('update-menu-drawer-item-refs');
    },

    isViewed() {
      if (this.isViewed) {
        this.handleTrackingTeaser(this.teaser, 'view_promotion');
      }
    },
  },

  mounted() {
    /**
     * Check teaser end date and display if valid.
     */
    setInterval(() => {
      const startTime = this.teaser.startTime;
      const endTime = this.teaser.endTime;
      let result = dateTime('false', 'false');

      if (startTime && endTime) {
        result = dateTime(startTime, endTime);
      }

      this.result = result;
    }, 1000);

    setTimeout(() => {
      this.loading = false;
    }, 1000);
  },

  methods: {
    /**
     * Copy promo code and toggle tooltip.
     */
    handleCopy() {
      const copyButton = this.$refs.copyButton;
      const tooltip = this.$refs.tooltip;

      if (!copyButton) {
        return;
      }

      navigator.clipboard.writeText(this.teaser.code);

      if (!tooltip) {
        return;
      }

      this.showTooltip = true;

      setTimeout(() => {
        this.showTooltip = false;
      }, this.$timing('message'));
    },

    /**
     * Handle link view for dataLayer.
     * @param {HTMLElement} teaser - Teaser item.
     * @param {String} eventType - Event type can be
     * view_promotion or select_promotion.
     */
    handleTrackingTeaser(teaser, eventType = 'view_promotion') {
      cnvs.EventBus.emit(`dataLayer:${eventType}`, {
        event: eventType,
        promotion: {
          promotion_id: teaser.url ? teaser.url : '',
          promotion_name: teaser.title ? teaser.title : '',
          creative_slot: this.position,
          creative_name: 'Menu Teaser',
        },
      });
    },
  },
};
</script>

<style lang="scss">
@import './menu-teaser';
</style>
